import React, { useState, useEffect, useRef } from 'react';
import { usePost, useFetchWithBody } from '../../api/reactQueryCustomHooks';
import { apiRoute } from '../../constants/enum';
import { useSelector } from "react-redux";
import { MainContainer } from '../../components';

const StockTally = () => {
  const [inputMode] = useState('manual');
  const [stockLocation, setStockLocation] = useState('');
  const [shape, setShape] = useState('');
  const [sizeFrom, setSizeFrom] = useState('');
  const [sizeTo, setSizeTo] = useState('');
  const [barcodeInput, setBarcodeInput] = useState('');
  const [currentStock, setCurrentStock] = useState([]);
  const [tallyStock, setTallyStock] = useState([]);
  const [allStockData, setAllStockData] = useState([]);
  const [showAlert, setShowAlert] = useState(false);

  const { compCode, dbUser } = useSelector((state) => state.company);

  // Get location data
  const { data: locationList = [] } = useFetchWithBody({
    url: apiRoute.packetTransferEntry.getFromLocation,
    queryKey: ["locationList"],
    inputData: {
      compCode: compCode
    }
  });

  // Get shape data
  const { data: shapeList = [] } = useFetchWithBody({
    url: apiRoute.shape.getShape,
    queryKey: ["shapeList"],
    inputData: {
      compCode: compCode
    }
  });

  // API call for getting stock data
  const { mutate: fetchStockData } = usePost({
    queryKey: "stockData",
    url: apiRoute.stockEntry.getStockTellyData,
  });

  // Update current stock whenever allStockData or tallyStock changes
  useEffect(() => {
    if (allStockData.length > 0) {
      const filteredCurrentStock = allStockData.filter(item => {
        return !tallyStock.some(tallyItem =>
          tallyItem.LotNo === item.LotNo && tallyItem.PktNo === item.PktNo
        );
      });

      setCurrentStock(filteredCurrentStock.map(item => ({
        LotNo: item.LotNo,
        PktNo: item.PktNo,
        Shape: item.ShapeName,
        Weight: item.BWeight
      })));
    }
  }, [allStockData, tallyStock]);

  const handleAddBarcode = () => {
    if (showAlert || !barcodeInput.trim()) return;

    // Split barcode into LotNo and PktNo (format: "PS#16")
    const [lotNo, pktNo] = barcodeInput.split('#');
    if (!lotNo || !pktNo) {
      setShowAlert(true);
      alert('Invalid barcode format. Please use format: LOT#PKT (e.g. PS#16)');
      setShowAlert(false);
      setBarcodeInput('');
      return;
    }

    // Find the item in current stock
    const item = currentStock.find(item =>
      item.LotNo === lotNo && item.PktNo === pktNo
    );

    if (item) {
      setTallyStock([...tallyStock, item]);
      setBarcodeInput('');
      if (inputRef.current) {
        inputRef.current.focus(); // safe to use the ref
      }
    } else {
      setShowAlert(true);
      alert('Item not found in current stock');
      setShowAlert(false);
      setBarcodeInput('');
    }
  };

  const handleClearTally = () => {
    setTallyStock([]);
  };

  const handleViewStock = () => {
    if (!stockLocation) {
      alert('Please select a stock location');
      return;
    }

    const fWeight = sizeFrom ? parseFloat(sizeFrom) : 0;
    const tWeight = sizeTo ? parseFloat(sizeTo) : 0;

    fetchStockData({
      data: {
        compcode: compCode,
        StkAt: stockLocation,
        tradeof: 'P',
        ShapeCode: shape,
        FWeight: fWeight,
        TWeight: tWeight
      }
    }, {
      onSuccess: (data) => {
        setAllStockData(data);
      },
      onError: (error) => {
        console.error("Error fetching stock data:", error);
      }
    });
  };
  const inputRef = useRef()
  // Calculate totals
  const currentStockTotalWeight = currentStock.reduce((sum, item) => sum + (item.Weight || 0), 0).toFixed(2);
  const tallyStockTotalWeight = tallyStock.reduce((sum, item) => sum + (item.Weight || 0), 0).toFixed(2);

  return (
    <MainContainer
      pageName={`Single Packet Stock Tally`}
      info={[]}
      breadcrumbs={[" Stock ", "Single Paycket Stock Tally"]}
    >
      <div className="min-h-screen bg-gray-50 ">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          {/* <div className="mb-">
            <h1 className="text-2xl font-bold text-gray-800 border-b-2 border-blue-500 pb-2">
              Single Packet Stock Tally
            </h1>
          </div> */}

          {/* Main Card Container */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            {/* Filters Section */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Stock Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div>
                  <label htmlFor="location" className="block text-sm text-gray-700 mb-1">Stock Location *</label>
                  <select
                    id="location"
                    value={stockLocation}
                    onChange={(e) => setStockLocation(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                    required
                  >
                    <option value="">Select Location</option>
                    {locationList?.map((loc) => (
                      <option key={loc.Party_Id} value={loc.Party_Id}>
                        {loc.Party_Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="shape" className="block text-sm text-gray-700 mb-1">Shape</label>
                  <select
                    id="shape"
                    value={shape}
                    onChange={(e) => setShape(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                  >
                    <option value="">All Shapes</option>
                    {shapeList?.map((shapeItem) => (
                      <option key={shapeItem.S_Code} value={shapeItem.S_Code}>
                        {shapeItem.S_Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="sizeFrom" className="block text-sm text-gray-700 mb-1">From Weight</label>
                  <input
                    type="number"
                    id="sizeFrom"
                    value={sizeFrom}
                    onChange={(e) => setSizeFrom(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                    placeholder="From"
                    step="0.01"
                  />
                </div>

                <div>
                  <label htmlFor="sizeTo" className="block text-sm text-gray-700 mb-1">To Weight</label>
                  <input
                    type="number"
                    id="sizeTo"
                    value={sizeTo}
                    onChange={(e) => setSizeTo(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                    placeholder="To"
                    step="0.01"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-2 rounded-md transition duration-200 text-sm"
                    onClick={handleViewStock}
                  >
                    View Stock
                  </button>
                </div>
              </div>
            </div>

            {/* Barcode Entry Section */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Barcode Entry</h2>
              <div className="flex">
                <input
                  type="text"
                  placeholder="Enter barcode (e.g. PS#16)"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border text-sm"
                  value={barcodeInput}
                  ref={inputRef}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddBarcode();
                    }
                  }}
                  onBlur={handleAddBarcode}
                  autoFocus
                />
              </div>
            </div>

            {/* Stock Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Current Stock */}
              <div className="bg-gray-50 rounded-lg shadow-sm overflow-hidden border border-gray-200">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-100">
                  <h3 className="text-lg font-medium text-gray-900">Current Stock</h3>
                  <p className="text-sm text-gray-500">{currentStock.length} items available</p>
                </div>
                <div className="overflow-x-auto" style={{ maxHeight: '400px' }}>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lot No</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pkt No</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shape</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentStock.map((item, index) => (
                        <tr key={`${item.LotNo}-${item.PktNo}-${index}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-700">{item.LotNo}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.PktNo}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.Shape}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.Weight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-gray-100 px-4 py-2 border-t border-gray-200 flex justify-between text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Total Packets:</span> <span className="font-bold">{currentStock.length} </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total Weight:</span> <span className="font-bold"> {currentStockTotalWeight} CT </span>
                  </div>
                </div>
              </div>

              {/* Tally Stock */}
              <div className="bg-blue-50 rounded-lg shadow-sm overflow-hidden border border-blue-100">
                <div className="px-4 py-3 border-b border-blue-200 bg-blue-100">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-blue-900">Tally Stock</h3>
                      <p className="text-sm text-blue-700">{tallyStock.length} items tallied</p>
                    </div>
                    <button
                      onClick={handleClearTally}
                      className="text-white hover:text-white bg-red-500 hover:bg-red-600 px-3 py-1 rounded text-sm font-medium transition-colors"
                    >
                      Clear All Tally
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto" style={{ maxHeight: '400px' }}>
                  <table className="min-w-full divide-y divide-blue-200">
                    <thead className="bg-blue-100">
                      <tr>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-blue-800 uppercase tracking-wider">Lot No</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-blue-800 uppercase tracking-wider">Pkt No</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-blue-800 uppercase tracking-wider">Shape</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-blue-800 uppercase tracking-wider">Weight (kg)</th>
                      </tr>
                    </thead>
                    <tbody className="bg-blue-50 divide-y divide-blue-200">
                      {tallyStock.map((item, index) => (
                        <tr key={`${item.LotNo}-${item.PktNo}-${index}`} className="hover:bg-blue-100">
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-blue-900">{item.LotNo}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-900">{item.PktNo}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-900">{item.Shape}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-900">{item.Weight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-blue-100 px-4 py-2 border-t border-blue-200 flex justify-between">
                  <div className="flex justify-between w-full">
                    <div className="text-sm">
                      <span className="font-medium text-blue-800">Total Packets:</span> <span className='font-bold text-blue-900'>{tallyStock.length}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium text-blue-800">Total Weight:</span> <span className='font-bold text-blue-900'>{tallyStockTotalWeight} CT</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default StockTally;